import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { XIcon } from 'lucide-react';
import { galleryImages, pastEvents } from '../data/events';
export function PastEvents() {
  const [lightbox, setLightbox] = useState<number | null>(null);
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setLightbox(null);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
  return <main className="w-full bg-paper" data-id="element-450" data-mp="ywlimh05qhv8">
      <section className="border-b border-paper-line bg-paper-warm" data-id="element-451" data-mp="k1x6wl45yn76">
        <div className="mx-auto max-w-content px-5 py-20 sm:px-8 sm:py-24" data-id="element-452" data-mp="k3rpvtvvk58m">
          <p className="eyebrow text-signal" data-id="element-453" data-mp="oynlf40blaqh">Past events</p>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-bold leading-[1.08] text-ink sm:text-5xl" data-id="element-454" data-mp="34rd2dtebv035">
            Where we have been, and what came out of it
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink-muted" data-id="element-455" data-mp="1xlrso83sbsxs">
            A record of the rooms we have convened, from mainstage sessions and side events to closed roundtables and
            field visits. Each one ended with something concrete: a mandate opened, a vehicle structured, a term sheet
            issued.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-content px-5 py-20 sm:px-8" data-id="element-456" data-mp="21pziy9pw5gy4">
        <h2 className="eyebrow text-ink-muted" data-id="element-457" data-mp="2gj2thx52eufc">Selected convenings</h2>
        <div className="mt-8 space-y-16" data-id="element-458" data-mp="2bkrkfomlgny6">
          {pastEvents.map((event, index) => <motion.article key={event.id} initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.5
        }} className="grid gap-8 border-b border-paper-line pb-16 last:border-0 last:pb-0 lg:grid-cols-[1fr_1.15fr] lg:gap-12" data-id="element-459" data-mp="2pdh7zxhru0xe" data-mpi={index}>
              <div className={index % 2 === 1 ? 'lg:order-2' : undefined} data-id="element-460" data-mp="1rzp8ljpnfatr">
                <div className="grid grid-cols-2 gap-3" data-id="element-461" data-mp="1w082djb3qqyl">
                  {event.images.map((src, imageIndex) => <button key={src + imageIndex} type="button" onClick={() => setLightbox(galleryImages.findIndex(image => image.src === src))} className="group relative overflow-hidden rounded-lg focus:outline-none focus:ring-2 focus:ring-signal/40" data-id="element-462" data-mp="20zc9m4eguema" data-mpi={imageIndex}>
                      <img src={src} alt={`${event.title}, ${event.city}`} className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" data-id="element-463" data-mp="2fqbccysme8xy" />
                    </button>)}
                </div>
              </div>

              <div className={index % 2 === 1 ? 'lg:order-1' : undefined} data-id="element-464" data-mp="ubns1l01x0pg">
                <p className="eyebrow text-ink-muted" data-id="element-465" data-mp="msoazm02bl99">
                  {format(new Date(event.date), 'MMMM yyyy')} · {event.city} · {event.format}
                </p>
                <h3 className="mt-3 font-display text-3xl font-semibold leading-snug text-ink" data-id="element-466" data-mp="d1jweneau2ey">{event.title}</h3>
                <p className="mt-4 text-base leading-relaxed text-ink-soft" data-id="element-467" data-mp="838l088cvjum">{event.summary}</p>
                <dl className="mt-7 grid gap-5 sm:grid-cols-3" data-id="element-468" data-mp="15r0lbb2pn16f">
                  {event.outcomes.map(outcome => <div key={outcome} className="border-t-2 border-signal/40 pt-3" data-id="element-469" data-mp="10sfkb9fmwdgx">
                      <dd className="text-sm leading-relaxed text-ink" data-id="element-470" data-mp="vrrrqjbrbokl">{outcome}</dd>
                    </div>)}
                </dl>
                <p className="mt-6 text-sm text-ink-muted" data-id="element-471" data-mp="rqfy82prij74">{event.attendees} attendees</p>
              </div>
            </motion.article>)}
        </div>
      </section>

      <section className="border-t border-paper-line bg-white" data-id="element-472" data-mp="3cg5cadin60yr">
        <div className="mx-auto max-w-content px-5 py-20 sm:px-8" data-id="element-473" data-mp="147qiklottuq8">
          <h2 className="font-display text-3xl font-semibold text-ink" data-id="element-474" data-mp="146fh4siusz2a">Gallery</h2>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-ink-muted" data-id="element-475" data-mp="1909yhpazl1y7">
            Moments from our convenings. Select an image to view it larger.
          </p>
          <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-476" data-mp="za75vq7s1q0e">
            {galleryImages.map((image, index) => <li key={image.src + index} data-id="element-477" data-mp="1mzjb6ezzhdxf" data-mpi={index}>
                <button type="button" onClick={() => setLightbox(index)} className="group block w-full text-left focus:outline-none focus:ring-2 focus:ring-signal/40" data-id="element-478" data-mp="1rycs6hehbn2c">
                  <div className="overflow-hidden rounded-lg" data-id="element-479" data-mp="1hy2d0r8vw5kj">
                    <img src={image.src} alt={image.caption} className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" data-id="element-480" data-mp="2idcsik9p1e9" />
                  </div>
                  <p className="mt-2.5 text-xs leading-relaxed text-ink-muted" data-id="element-481" data-mp="3q7a8hxq36uy0">{image.caption}</p>
                </button>
              </li>)}
          </ul>
        </div>
      </section>

      {lightbox !== null && galleryImages[lightbox] && <div role="dialog" aria-modal="true" aria-label={galleryImages[lightbox].caption} className="fixed inset-0 z-50 flex items-center justify-center bg-ink/90 p-5" onClick={() => setLightbox(null)} data-id="element-482" data-mp="40lgrzxsaqa7">
          <motion.figure initial={{
        opacity: 0,
        scale: 0.97
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.25
      }} className="max-h-full w-full max-w-4xl" onClick={clickEvent => clickEvent.stopPropagation()} data-id="element-483" data-mp="3m66vrk3c5f10">
            <img src={galleryImages[lightbox].src} alt={galleryImages[lightbox].caption} className="max-h-[75vh] w-full rounded-lg object-contain" data-id="element-484" data-mp="2oqt0tz34o0my" />
            <figcaption className="mt-4 flex items-center justify-between gap-6 text-sm text-white/70" data-id="element-485" data-mp="3ifiea2dnyonx">
              {galleryImages[lightbox].caption}
              <button type="button" onClick={() => setLightbox(null)} className="inline-flex items-center gap-1.5 rounded-full border border-white/30 px-4 py-2 text-white transition-colors hover:border-white/70" data-id="element-486" data-mp="3n8tkc1anj1es">
                <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-487" data-mp="2zho61qkd0h6l" />
                Close
              </button>
            </figcaption>
          </motion.figure>
        </div>}
    </main>;
}